import { useState } from "react";
import { Button } from "@/components/ui/button";
import { UserPlus, UserCheck, UserX, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";

interface FriendRequestButtonProps {
  targetUserId: string;
  currentUserId: string;
}

export const FriendRequestButton = ({ targetUserId, currentUserId }: FriendRequestButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Check friendship status
  const { data: friendship } = useQuery({
    queryKey: ['friendship-status', currentUserId, targetUserId],
    queryFn: async () => {
      // Check if they're already friends (mutual connection)
      const { data: areFriends } = await supabase
        .rpc('are_friends', { user1_id: currentUserId, user2_id: targetUserId });

      if (areFriends) return { status: 'friends' };

      // Check for pending request
      const { data: requests } = await supabase
        .from('friend_requests')
        .select('*')
        .or(`and(requester_id.eq.${currentUserId},requested_id.eq.${targetUserId}),and(requester_id.eq.${targetUserId},requested_id.eq.${currentUserId})`)
        .eq('status', 'pending')
        .maybeSingle();

      if (requests) {
        if (requests.requester_id === currentUserId) {
          return { status: 'sent', requestId: requests.id };
        } else {
          return { status: 'received', requestId: requests.id };
        }
      }

      return { status: 'none' };
    },
    enabled: currentUserId !== targetUserId
  });

  const sendFriendRequest = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('friend_requests')
        .insert({
          requester_id: currentUserId,
          requested_id: targetUserId,
          message: 'Would like to connect with you!'
        });

      if (error) throw error;

      toast({
        title: "Friend request sent!",
        description: "Your request has been sent successfully."
      });

      queryClient.invalidateQueries({ queryKey: ['friendship-status'] });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send friend request.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const acceptFriendRequest = async (requestId: string) => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .rpc('accept_friend_request', { request_id: requestId });

      if (error) throw error;

      toast({
        title: "Friend request accepted!",
        description: "You are now friends."
      });

      queryClient.invalidateQueries({ queryKey: ['friendship-status'] });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to accept friend request.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const declineFriendRequest = async (requestId: string) => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('friend_requests')
        .update({ status: 'declined', responded_at: new Date().toISOString() })
        .eq('id', requestId);

      if (error) throw error;

      toast({
        title: "Friend request declined",
        description: "The request has been declined."
      });

      queryClient.invalidateQueries({ queryKey: ['friendship-status'] });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to decline friend request.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (currentUserId === targetUserId) return null;

  if (!friendship) {
    return (
      <Button variant="outline" size="sm" disabled>
        <Clock className="w-4 h-4 mr-2" />
        Loading...
      </Button>
    );
  }

  switch (friendship.status) {
    case 'friends':
      return (
        <Button variant="secondary" size="sm" disabled>
          <UserCheck className="w-4 h-4 mr-2" />
          Friends
        </Button>
      );

    case 'sent':
      return (
        <Button variant="outline" size="sm" disabled>
          <Clock className="w-4 h-4 mr-2" />
          Request Sent
        </Button>
      );

    case 'received':
      return (
        <div className="flex gap-2">
          <Button 
            size="sm" 
            onClick={() => acceptFriendRequest(friendship.requestId!)}
            disabled={isLoading}
          >
            <UserCheck className="w-4 h-4 mr-2" />
            Accept
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => declineFriendRequest(friendship.requestId!)}
            disabled={isLoading}
          >
            <UserX className="w-4 h-4 mr-2" />
            Decline
          </Button>
        </div>
      );

    default:
      return (
        <Button 
          size="sm" 
          onClick={sendFriendRequest}
          disabled={isLoading}
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Friend
        </Button>
      );
  }
};